import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import classes from './DatePickerWithLabelRanged.module.css'
import Button from '../Button'
import OutsideClickComponent from '../../fragments/OutsideClickComponent'
import { formatDate, formatDateLocalRange } from '../../../lib/script'
import CustomDatePickerRange from './CustomDatePickerRange'
import { fitFixedPopoverToViewport } from '../../../lib/fitFixedPopoverToViewport'

export default function DatePickerWithLabelRanged({label, action, identifier, fullWidth, preSelectedValue, minMax}) {
    const [dateState, setDateState] = useState(false)
    const triggerRef = useRef(null)
    const modalRef = useRef(null)
    const [modalPosition, setModalPosition] = useState({ top: 0, left: 0 })

    const [dateObj, setDateObj] = useState({
        isRange: false,
        start: null,
        end: null
    })

    const updateModalPosition = useCallback(() => {
        if (!triggerRef.current) {
            return
        }
        const next = fitFixedPopoverToViewport({
            triggerRect: triggerRef.current.getBoundingClientRect(),
            popoverWidth: modalRef.current?.offsetWidth,
            popoverHeight: modalRef.current?.offsetHeight,
        })
        setModalPosition(next)
    }, [])

    function openDateHandler() {
        updateModalPosition()
        setDateState(true)
    }

    function closeDateHandler() {
        // OutsideClick wraps the trigger even when the calendar is closed.
        // Ignore those clicks so History/Appointments lists are not refreshed on every page click.
        if (!dateState) {
            return
        }
        setDateState(false)

        if (!action) {
            return
        }

        const nextValue = dateObj.start
            ? (dateObj.isRange
                ? `${formatDate(dateObj.start)}|${formatDate(dateObj.end)}`
                : formatDate(dateObj.start))
            : null
        const prevValue = preSelectedValue ?? null
        if (nextValue === prevValue) {
            return
        }

        action({
            data: {
                value: nextValue
            },
            identifier: identifier,
            option: {
                label: nextValue,
                value: nextValue
            }
        })
    }

    function setDateHandler(range) {
        let obj = {
            isRange: false,
            start: null,
            end: null
        }

        if (range) {
            if (range.startDate && range.endDate) {

                if (range.startDate == range.endDate) {
                    obj.isRange = false
                    obj.start = formatDateLocalRange(range.startDate)
                    obj.end = null

                    setDateObj(obj)
                } else {
                    obj.isRange = true
                    obj.start = formatDateLocalRange(range.startDate)
                    obj.end = formatDateLocalRange(range.endDate)

                    setDateObj(obj)
                }

            }
        }

    }

    function removeDateHandler() {
        if (action) {
            action({
                data: {
                    value: null
                },             
                identifier: identifier,
                option: {
                    label:null,
                    value: null
                }
            })
        }

        setDateObj({
            isRange: false,
            start: null,
            end: null
        })
    }


    useEffect(() => {
        if (preSelectedValue) {
            if (preSelectedValue.includes('|')) {
                let dateArray = preSelectedValue.split('|')
                setDateObj({
                    isRange: true,
                    start: formatDateLocalRange(dateArray[0]),
                    end: formatDateLocalRange(dateArray[1])
                })
            } else {
                setDateObj({
                    isRange: false,
                    start: formatDateLocalRange(preSelectedValue),
                    end: null
                })
            }
        } else {
            setDateObj({
                isRange: false,
                start: null,
                end: null
            })
        }
    }, [preSelectedValue])

    useLayoutEffect(() => {
        if (!dateState) {
            return
        }
        updateModalPosition()
        const raf = window.requestAnimationFrame(() => updateModalPosition())
        const onReposition = () => updateModalPosition()
        window.addEventListener('resize', onReposition)
        window.addEventListener('scroll', onReposition, true)
        return () => {
            window.cancelAnimationFrame(raf)
            window.removeEventListener('resize', onReposition)
            window.removeEventListener('scroll', onReposition, true)
        }
    }, [dateState, updateModalPosition])

    const outsideClickExtraInsideRefs = useMemo(() => [modalRef], [])
    const portalRoot = typeof document !== 'undefined'
        ? (document.getElementById('overlay-root') || document.getElementById('datepicker-portal') || document.body)
        : null

    const calendarModal = dateState ? (
        <div
            ref={modalRef}
            className={classes.modal_portal}
            style={{ top: modalPosition.top, left: modalPosition.left }}
        >
            <CustomDatePickerRange setDate={setDateHandler} preSelectedValue={preSelectedValue} minMax={minMax}/>
        </div>
    ) : null

    return <div className={`${classes.container} ${fullWidth ? classes.full_width : ''}`} >
        <OutsideClickComponent task={closeDateHandler} extraInsideRefs={outsideClickExtraInsideRefs}>
            <div className={classes.container_inner} ref={triggerRef}>
                
                <Button click={openDateHandler} btnStyle={'btn_light_grey'} noHover={true}>
                    {label ? label : 'Date'}
                    {dateObj && <>
                        {dateObj.isRange && <>: From: {dateObj.start} to {dateObj.end}</>}
                        {!dateObj.isRange && <>: {dateObj.start}</>}
                    </>}
                </Button>
                {preSelectedValue && <Button click={removeDateHandler} btnStyle={'btn_simple'} title={'remove'} noHover={true}><i className="ti ti-x"></i></Button>}

            </div>

            {calendarModal && portalRoot
                ? ReactDOM.createPortal(calendarModal, portalRoot)
                : calendarModal}
        </OutsideClickComponent>
    </div>
}
